import { useQuery } from "@tanstack/react-query";
import { useAuth, useClerk } from "@clerk/react";
import { Link, Redirect } from "wouter";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface AccessStatus {
  status: "approved" | "pending" | "rejected" | "none";
  email: string | null;
  isAdmin: boolean;
}

async function fetchAccessStatus(): Promise<AccessStatus> {
  const response = await fetch("/api/early-access/status", {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("We couldn’t verify your access. Please try again.");
  }
  return response.json() as Promise<AccessStatus>;
}

export function PlatformAccessGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { signOut } = useClerk();
  const access = useQuery({
    queryKey: ["/api/early-access/status", userId],
    queryFn: fetchAccessStatus,
    enabled: isLoaded && Boolean(isSignedIn && userId),
    retry: 1,
  });

  if (!isLoaded) return <AccessLoading />;
  if (!isSignedIn) return <Redirect to="/apply" />;
  if (access.isPending) return <AccessLoading />;

  if (access.isError) {
    return (
      <AccessMessage
        title="Access could not be verified"
        description="Please check your connection and try again. Your canvas remains private while access is being checked."
        onSignOut={() => signOut({ redirectUrl: "/" })}
      />
    );
  }

  if (access.data.status === "approved") return <>{children}</>;

  if (access.data.status === "pending") {
    return (
      <AccessMessage
        title="Your application is being reviewed"
        description="We’ve received your early access request and will email you when there’s an update."
        email={access.data.email}
        onSignOut={() => signOut({ redirectUrl: "/" })}
      />
    );
  }

  if (access.data.status === "rejected") {
    return (
      <AccessMessage
        title="We can’t offer access right now"
        description="Thank you for your interest. You can submit an updated application if your circumstances have changed."
        email={access.data.email}
        onSignOut={() => signOut({ redirectUrl: "/" })}
      />
    );
  }

  return (
    <AccessMessage
      title="Request early access"
      description="The Lifecycle Canvas is currently available to approved early access applicants."
      email={access.data.email}
      onSignOut={() => signOut({ redirectUrl: "/" })}
    />
  );
}

function AccessMessage({
  title,
  description,
  email,
  onSignOut,
}: {
  title: string;
  description: string;
  email?: string | null;
  onSignOut: () => void;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 px-6 py-12 text-slate-200">
      <section className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center shadow-2xl">
        <img src="/logo-40.png" alt="" className="mx-auto h-10 w-10" />
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-teal-300">
          The Lifecycle Canvas
        </p>
        <h1 className="mt-2 text-2xl font-semibold text-white">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-400">{description}</p>
        {email && (
          <p className="mt-5 rounded-lg border border-slate-800 bg-slate-950/70 px-4 py-3 text-sm text-slate-300">
            Signed in as <span className="font-medium text-white">{email}</span>
          </p>
        )}
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <Link href="/apply">
            <Button className="w-full bg-teal-700 text-white hover:bg-teal-600 sm:w-auto">
              Apply for early access
            </Button>
          </Link>
          <Button
            type="button"
            variant="outline"
            onClick={onSignOut}
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
          >
            Sign out
          </Button>
        </div>
      </section>
    </main>
  );
}

function AccessLoading() {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-slate-950 text-sm text-slate-400"
      aria-live="polite"
    >
      Checking early access…
    </main>
  );
}
